import { useCallback, useMemo } from "react";
import type { DifyAPITestRun, QuestionTableList } from "~/components/difyAPITest/difyAPITest.types";
import {
  buildRunCsvFileName,
  buildCsvRowsFromRun,
  buildDifyResultCsvText,
} from "~/services/difyAPITest/difyAPITestRunService";
import { formatDateTimeForDisplay } from "~/utils/difyAPITest/difyAPITestDateTimeUtils";
import { downloadCsvFile } from "~/utils/csvDownloadUtils";

/**
 * 実行履歴表示とCSV出力データを管理するhook
 * @param runs 実行履歴一覧
 * @return 表示用履歴一覧とCSV出力関数
 */
export const useDifyAPITestHistory = (runs: DifyAPITestRun[]) => {
  const questionTableList = useMemo<QuestionTableList[]>(() => {
    return [...runs].reverse().map((run) => {
      const csvData = buildCsvRowsFromRun(run);
      const completedCount = run.jobs.filter((job) => job.status === "completed").length;
      const errorCount = run.jobs.filter((job) => job.status === "error").length;

      return {
        runId: run.runId,
        title: run.title,
        createdAt: run.createdAt,
        createdAtLabel: formatDateTimeForDisplay(run.createdAt),
        status: run.status,
        totalCount: run.jobs.length,
        completedCount,
        errorCount,
        csvData,
      };
    });
  }, [runs]);

  /**
   * 指定した実行履歴のCSVをダウンロードする
   * @param runId 実行ID
   * @return ダウンロード処理の完了
   */
  const downloadRunCsv = useCallback(
    (runId: string) => {
      const targetRun = runs.find((run) => run.runId === runId);

      if (!targetRun) {
        return;
      }

      const csvRows = buildCsvRowsFromRun(targetRun);
      const fileName = buildRunCsvFileName(targetRun);
      const csvText = buildDifyResultCsvText(csvRows);

      downloadCsvFile(fileName, csvText);
    },
    [runs]
  );

  return {
    questionTableList,
    downloadRunCsv,
  };
};
